import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faPencilAlt, faPlus, faGripLines, faTrash, faTimes, faHeading, faParagraph, faQuoteLeft, faHashtag } from '@fortawesome/free-solid-svg-icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ThemeContext from '../context/ThemeContext';

const TEMPLATE_TYPES = [ { id: 'post', name: 'Пост' }, { id: 'note', name: 'Заметка' }, { id: 'interview', name: 'Интервью' }, { id: 'summary', name: 'Саммари' }, { id: 'editing', name: 'Редактирование' }];
const BLOCK_TYPES = {
  heading: { id: 'heading', name: 'Заголовок', icon: faHeading, example: 'Сделай заголовок ярким и интригующим.' },
  mainText: { id: 'mainText', name: 'Основная часть', icon: faParagraph, example: 'Напиши основной текст, разбив его на абзацы.' },
  quote: { id: 'quote', name: 'Цитата', icon: faQuoteLeft, example: 'Найди вдохновляющую цитату по теме.' },
  hashtags: { id: 'hashtags', name: 'Хэштеги', icon: faHashtag, example: 'Подбери 5-7 релевантных хэштегов.' },
  exclusions: { id: 'exclusions', name: 'Исключения', icon: faTimes, example: 'Не включай названия блоков (например, "Заголовок:", "Основная часть:"). Генерируй содержимое каждого блока строго по порядку, разделяя их пустой строкой.' },
};

const DraggableBlock = ({ block, index, moveBlock, onEdit, onRemove }) => {
    const [{ isDragging }, drag] = useDrag({ type: 'BLOCK', item: { id: block.id, index }, collect: (monitor) => ({ isDragging: !!monitor.isDragging() }) });
    const [, drop] = useDrop({ accept: 'BLOCK', hover: (draggedItem) => { if (draggedItem.index !== index) { moveBlock(draggedItem.index, index); draggedItem.index = index; } } });
    const blockInfo = BLOCK_TYPES[block.type];
    return (
        <div ref={(node) => drag(drop(node))} className={`block-item card mb-2 ${isDragging ? 'is-dragging' : ''}`} style={{ opacity: isDragging ? 0.5 : 1 }}>
            <div className="card-body p-3 d-flex align-items-center">
                <div className="drag-handle me-3 text-muted"><FontAwesomeIcon icon={faGripLines} /></div>
                <FontAwesomeIcon icon={blockInfo?.icon || faPlus} className="me-3 text-primary" style={{width: '20px'}} />
                <div className="block-content flex-grow-1 fw-bold">{blockInfo?.name || 'Неизвестный блок'}</div>
                <div className="block-actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => onEdit(block, index)}><FontAwesomeIcon icon={faPencilAlt} /></button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(index)}><FontAwesomeIcon icon={faTrash} /></button>
                </div>
            </div>
        </div>
    );
};

const PaletteBlock = ({ type }) => {
    const [{ isDragging }, drag] = useDrag({ type: 'PALETTE_BLOCK', item: { type }, collect: (monitor) => ({ isDragging: !!monitor.isDragging() }) });
    const blockInfo = BLOCK_TYPES[type];
    return (
        <div ref={drag} className="palette-block" style={{ opacity: isDragging ? 0.5 : 1 }}>
            <FontAwesomeIcon icon={blockInfo.icon} className="palette-block-icon" />
            <span className="palette-block-name">{blockInfo.name}</span>
        </div>
    );
};

const BlockSettingsModal = ({ block, onSave, onCancel }) => {
    const [settings, setSettings] = useState(block.settings || { description: '', count: block.type === 'hashtags' ? 5 : undefined });
    const handleSettingsChange = (field, value) => setSettings({ ...settings, [field]: value });
    const handleSave = () => { onSave({ ...block, settings }); onCancel(); };
    const blockInfo = BLOCK_TYPES[block.type];

    return (
        <div className="modal-backdrop-custom" onMouseDown={onCancel}>
            <div className="modal-content-custom" onMouseDown={e => e.stopPropagation()}>
                <button onMouseDown={onCancel} className="modal-close-btn"><FontAwesomeIcon icon={faTimes} /></button>
                <h3>Настройки блока: {blockInfo?.name}</h3>
                <hr/>
                <div className="mb-3">
                    <label className="form-label">Инструкция для этого блока</label>
                    <textarea className="form-control" rows="3" value={settings.description || ''} onChange={(e) => handleSettingsChange('description', e.target.value)} placeholder={blockInfo?.example}></textarea>
                </div>
                {block.type === 'hashtags' && (
                    <div className="mb-3">
                        <label className="form-label">Количество хэштегов</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            value={settings.count || ''} 
                            onChange={(e) => {
                                const value = e.target.value;
                                // Разрешаем пустое поле или только цифры
                                if (value === '' || /^\d+$/.test(value)) {
                                    // Если пустое - сохраняем пустое, если число - проверяем диапазон
                                    if (value === '') {
                                        handleSettingsChange('count', '');
                                    } else {
                                        const num = parseInt(value);
                                        if (num >= 1 && num <= 30) {
                                            handleSettingsChange('count', num);
                                        } else if (value.length <= 2) {
                                            // Разрешаем ввод до 2 цифр для промежуточного состояния
                                            handleSettingsChange('count', value);
                                        }
                                    }
                                }
                            }} 
                            placeholder="Введите число от 1 до 30"
                            onBlur={(e) => {
                                // При потере фокуса устанавливаем значение по умолчанию если поле пустое
                                if (e.target.value === '' || parseInt(e.target.value) < 1 || parseInt(e.target.value) > 30) {
                                    handleSettingsChange('count', 5);
                                }
                            }}
                        />
                    </div>
                )}
                <div className="d-flex justify-content-end mt-4">
                    <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>Отмена</button>
                    <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
                </div>
            </div>
        </div>
    );
};

const TemplateFormLayout = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const { theme } = useContext(ThemeContext);
    
    const [formData, setFormData] = useState({ 
        title: '', 
        description: '', 
        category: '', 
        promptType: 'post', 
        blocks: [], 
        settings: { textStyle: 'neutral', variability: 0.7 } 
    });
    
    // НОВОЕ: Отдельное состояние для контента промпта и режима редактирования
    const [promptContent, setPromptContent] = useState('');
    const [isEditingPrompt, setIsEditingPrompt] = useState(false);
    const [promptBeforeEdit, setPromptBeforeEdit] = useState('');
    // НОВОЕ: Флаг, указывающий, что промпт был изменен вручную и не должен синхронизироваться с блоками
    const [isPromptManuallySet, setIsPromptManuallySet] = useState(false);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [editingBlock, setEditingBlock] = useState(null); // Состояние теперь { block: ..., index: ... }

    const { title, description, category, promptType, blocks, settings } = formData;
    
    // ИЗМЕНЕНО: Функция генерации обернута в useCallback для стабильности
    const generatePromptContent = React.useCallback((data) => {
        const { promptType, blocks, settings } = data;
        const TEMPLATE_TYPE_NAMES = { post: 'пост', note: 'заметку', interview: 'интервью', summary: 'саммари', editing: 'отредактированный текст' };
        const TEXT_STYLE_NAMES = { 
            neutral: 'нейтральный', 
            formal: 'формальный', 
            friendly: 'дружелюбный', 
            professional: 'профессиональный', 
            creative: 'креативный' 
        };
        let content = `Ты профессиональный редактор-копирайтер. Твоя задача — создать ${TEMPLATE_TYPE_NAMES[promptType] || 'текст'} на основе предоставленного контекста.`;
        if (blocks.length > 0) {
            const sortedBlocks = [...blocks].sort((a, b) => {
                if (a.type === 'exclusions') return 1;
                if (b.type === 'exclusions') return -1;
                return 0;
            });
            
            const structureBlocks = sortedBlocks.filter(block => block.type !== 'exclusions');
            const exclusionBlocks = sortedBlocks.filter(block => block.type === 'exclusions');
            
            if (structureBlocks.length > 0) {
                content += `\n\n### СТРУКТУРА ОТВЕТА:`;
                structureBlocks.forEach((block, index) => {
                    const blockInfo = BLOCK_TYPES[block.type];
                    content += `\n\n${index + 1}. **${blockInfo?.name || 'Блок'}** (генерируй ТОЛЬКО содержимое без названия):`;
                    if (block.settings?.description) {
                        content += `\n   - Инструкция: ${block.settings.description}`;
                    } else if (block.type === 'hashtags') {
                        const count = block.settings?.count || 5;
                        content += `\n   - Инструкция: Подбери ${count} релевантных хэштегов`;
                    }
                });
            }
            
            if (exclusionBlocks.length > 0) {
                content += `\n\n### ВАЖНЫЕ ОГРАНИЧЕНИЯ:`;
                exclusionBlocks.forEach((block) => {
                    if (block.settings?.description) {
                        content += `\n- ${block.settings.description}`;
                    } else {
                        content += `\n- ${BLOCK_TYPES.exclusions.example}`;
                    }
                });
            }
        }
        content += `\n\n### ОБЩИЕ ПРАВИЛА:\n- Стиль текста: ${TEXT_STYLE_NAMES[settings.textStyle] || settings.textStyle}.`;
        content += `\n\nВажно: Не добавляй от себя лишних заголовков или пояснений. Генерируй только содержимое согласно структуре.`;
        return content;
    }, []);

    // НОВОЕ: Вычисляем "свежий" промпт при каждом изменении блоков/настроек
    const generatedPrompt = React.useMemo(() => generatePromptContent(formData), [formData, generatePromptContent]);
    
    // НОВОЕ: Эффект для синхронизации, если мы НЕ в режиме ручного редактирования
    useEffect(() => {
        // Синхронизируем, только если пользователь не редактирует промпт И не сохранил его вручную
        if (!isEditingPrompt && !isPromptManuallySet) {
            setPromptContent(generatedPrompt);
        }
    }, [generatedPrompt, isEditingPrompt, isPromptManuallySet]);

    useEffect(() => {
        if (isEditMode) {
            setLoading(true);
            axios.get(`/api/prompts/${id}`)
                .then(res => {
                    const fetchedData = {
                        ...res.data,
                        blocks: res.data.blocks.map(b => ({...b, id: b._id || `block-${Math.random()}`}))
                    };
                    setFormData(fetchedData);
                    setPromptContent(res.data.content);
                    // Если контент из базы данных отличается от сгенерированного, считаем его "ручным"
                    if (res.data.content !== generatePromptContent(fetchedData)) {
                        setIsPromptManuallySet(true);
                    }
                })
                .catch(() => setError('Не удалось загрузить шаблон'))
                .finally(() => setLoading(false));
        } else {
            // Для новых шаблонов устанавливаем блок по умолчанию и сбрассиваем форму
            const defaultBlocks = [{ 
                type: 'exclusions', 
                settings: { 
                    description: 'Не включай названия блоков (например, "Заголовок:", "Основная часть:") в свой ответ. Генерируй содержимое каждого блока строго по порядку, разделяя их пустой строкой.' 
                }, 
                id: `block-${Date.now()}` 
            }];
            const initialData = { 
                title: '', 
                description: '', 
                category: '', 
                promptType: 'post', 
                blocks: defaultBlocks, 
                settings: { textStyle: 'neutral', variability: 0.7 } 
            };
            setFormData(initialData);
            setPromptContent(generatePromptContent(initialData));
            setLoading(false);
        }
    }, [id, isEditMode, generatePromptContent]);

    const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleSettingsChange = (field, value) => {
        setFormData({ ...formData, settings: { ...formData.settings, [field]: (field === 'variability' ? parseFloat(value) : value) }});
        setIsPromptManuallySet(false); // Сбрасываем флаг при изменении настроек
    };
    
    const addBlock = (type) => {
        const newBlock = { type, settings: {}, id: `block-${Date.now()}` };
        setFormData(prev => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
        setIsPromptManuallySet(false); // Сбрасываем флаг
    };
    
    const removeBlock = (index) => {
        setFormData(prev => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== index) }));
        setIsPromptManuallySet(false); // Сбрасываем флаг
    };
    
    const moveBlock = (from, to) => {
        setFormData(prev => {
            const updatedBlocks = [...prev.blocks];
            const [moved] = updatedBlocks.splice(from, 1);
            updatedBlocks.splice(to, 0, moved);
            return { ...prev, blocks: updatedBlocks };
        });
        setIsPromptManuallySet(false); // Сбрасываем флаг
    };
    
    // ИСПРАВЛЕНИЕ: Мы сохраняем и блок, и его индекс
    const handleEditBlock = (block, index) => {
        setEditingBlock({ block, index });
    };

    const saveBlockSettings = (updatedBlock) => {
        setFormData(prev => {
            const newBlocks = [...prev.blocks];
            // Используем индекс из состояния editingBlock для точного обновления
            newBlocks[editingBlock.index] = updatedBlock;
            return { ...prev, blocks: newBlocks };
        });
        setIsPromptManuallySet(false); // Сбрасываем флаг
    };

    const [{ isOver }, drop] = useDrop(() => ({ accept: 'PALETTE_BLOCK', drop: (item) => addBlock(item.type), collect: (monitor) => ({ isOver: !!monitor.isOver() }) }));
    
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !category.trim()) { setError('Название и категория обязательны'); return; }
        setSubmitting(true);
        setError('');
        try {
            // Убираем временный 'id' перед отправкой на сервер
            const blocksToSubmit = blocks.map(({ id, ...rest }) => rest);
            // ИЗМЕНЕНО: Отправляем `promptContent` на сервер
            const dataToSubmit = { 
                title, 
                description, 
                category, 
                promptType, 
                blocks: blocksToSubmit, 
                settings,
                content: promptContent // Отправляем отредактированный или сгенерированный контент
            };
            
            if (isEditMode) await axios.put(`/api/prompts/${id}`, dataToSubmit);
            else await axios.post('/api/prompts', dataToSubmit);
            navigate('/templates');
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка сохранения');
            setSubmitting(false);
        }
    };

    // НОВЫЕ функции для управления редактированием промпта
    const handleEditPrompt = () => {
        setPromptBeforeEdit(promptContent);
        setIsEditingPrompt(true);
    };

    const handleCancelEditPrompt = () => {
        setPromptContent(promptBeforeEdit);
        setIsEditingPrompt(false);
    };

    const handleSaveChangesPrompt = () => {
        setIsEditingPrompt(false); 
        setIsPromptManuallySet(true); // Устанавливаем флаг, что контент теперь "ручной"
    };
    
    const handleRestorePrompt = () => {
      setPromptContent(generatedPrompt);
      setIsPromptManuallySet(false); // Возвращаемся к автоматической синхронизации
    };

    const availableBlocks = Object.keys(BLOCK_TYPES);

    if (loading) return <div className="d-flex justify-content-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Загрузка...</span></div></div>;

    const isPromptOutOfSync = isPromptManuallySet && promptContent !== generatedPrompt;

    return (
        <div className="template-form-container mt-4">
          <form onSubmit={onSubmit}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h1 className="h3">{isEditMode ? 'Редактирование шаблона' : 'Создание шаблона'}</h1>
              <div>
                <Link to="/templates" className="btn btn-outline-secondary me-2"><FontAwesomeIcon icon={faArrowLeft} className="me-2" />Назад</Link>
                <button type="submit" className="btn btn-primary" disabled={submitting}><FontAwesomeIcon icon={faSave} className="me-2" />{submitting ? 'Сохранение...' : (isEditMode ? 'Сохранить' : 'Создать')}</button>
              </div>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="template-form-layout">
              <div className="template-form-main">
                <div className="card">
                  <div className="card-header"><h5 className="mb-0">Общие сведения</h5></div>
                  <div className="card-body">
                    <div className="mb-3"><label htmlFor="title" className="form-label">Название*</label><input type="text" className="form-control" id="title" name="title" value={title} onChange={handleFormChange} placeholder="Например, Пост для Telegram" required /></div>
                    <div className="mb-3"><label htmlFor="promptType" className="form-label">Тип шаблона*</label><select className="form-select" id="promptType" name="promptType" value={promptType} onChange={(e) => setFormData({...formData, promptType: e.target.value, blocks:[]})} required>{TEMPLATE_TYPES.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></div>
                    <div className="mb-3"><label htmlFor="category" className="form-label">Категория*</label><input type="text" className="form-control" id="category" name="category" value={category} onChange={handleFormChange} placeholder="Маркетинг, Новости, Общее..." required /></div>
                    <div className="mb-3"><label htmlFor="description" className="form-label">Описание</label><input type="text" className="form-control" id="description" name="description" value={description} onChange={handleFormChange} placeholder="Для чего этот шаблон?" /></div>
                  </div>
                </div>
                <div className="card">
                    <div className="card-header"><h5 className="mb-0">Структура шаблона</h5></div>
                    <div className="card-body">
                        <div ref={drop} className={`template-blocks-container ${isOver ? 'is-over' : ''}`}>
                            {blocks.length === 0 ? <div className="empty-drop-zone"><FontAwesomeIcon icon={faPlus} size="2x" className="mb-3"/><p>Перетащите блоки из палитры сюда</p></div> : blocks.map((block, index) => <DraggableBlock key={block.id} block={block} index={index} moveBlock={moveBlock} onEdit={handleEditBlock} onRemove={removeBlock} />)}
                        </div>
                    </div>
                </div>
              </div>

              <div className="template-form-sidebar">
                <div className="card">
                    <div className="card-header"><h5 className="mb-0">Палитра блоков</h5></div>
                    <div className="card-body">
                        <div className="palette-blocks-grid">
                            {availableBlocks.map(type => <PaletteBlock key={type} type={type} />)}
                        </div>
                    </div>
                </div>
                <div className="card">
                    <div className="card-header"><h5 className="mb-0">Общие настройки</h5></div>
                    <div className="card-body">
                        <div className="mb-3"><label className="form-label">Стиль текста</label><select className="form-select" value={settings.textStyle} onChange={(e) => handleSettingsChange('textStyle', e.target.value)}><option value="neutral">Нейтральный</option><option value="formal">Формальный</option><option value="friendly">Дружелюбный</option><option value="professional">Профессиональный</option><option value="creative">Креативный</option></select></div>
                        <div><label className="form-label">Вариативность</label><div className="d-flex align-items-center"><input type="range" className="form-range" min="0" max="2" step="0.1" value={settings.variability} onChange={(e) => handleSettingsChange('variability', e.target.value)} /><span className="badge bg-secondary ms-3">{Number(settings.variability).toFixed(1)}</span></div></div>
                    </div>
                </div>
                <div className="card prompt-preview">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Предпросмотр промпта</h5>
                        <div>
                            {isEditingPrompt ? (
                                <>
                                    <button type="button" className="btn btn-sm btn-success me-2" onClick={handleSaveChangesPrompt} title="Сохранить изменения">
                                        <FontAwesomeIcon icon={faSave} />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={handleCancelEditPrompt} title="Отменить изменения">
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleEditPrompt} title="Редактировать промпт вручную">
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                         {isPromptOutOfSync && !isEditingPrompt && (
                            <div className="alert alert-warning p-2 mb-3 d-flex justify-content-between align-items-center">
                                <small>Промпт изменен вручную.</small>
                                <button className="btn btn-sm btn-outline-primary" onClick={handleRestorePrompt}>Восстановить из блоков</button>
                            </div>
                        )}
                        {isEditingPrompt ? (
                            <textarea 
                                className="form-control font-monospace"
                                value={promptContent}
                                onChange={(e) => setPromptContent(e.target.value)}
                                rows={15}
                            />
                        ) : (
                            <div className="prompt-preview-wrapper">
                                <SyntaxHighlighter 
                                    language="markdown" 
                                    style={theme === 'dark' ? oneDark : oneLight}
                                    customStyle={{
                                        margin: 0,
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        backgroundColor: 'transparent',
                                        fontSize: '0.85rem',
                                        maxHeight: '400px', // Ограничиваем высоту
                                        overflowY: 'auto'
                                    }}
                                    wrapLongLines={true}
                                >
                                    {promptContent}
                                </SyntaxHighlighter>
                            </div>
                        )}
                    </div>
                </div>
              </div>
            </div>
          </form>
          {editingBlock && <BlockSettingsModal block={editingBlock.block} onSave={saveBlockSettings} onCancel={() => setEditingBlock(null)} />}
        </div>
    );
};

const TemplateForm = () => {
  return (
    <DndProvider backend={HTML5Backend}>
      <TemplateFormLayout />
    </DndProvider>
  );
};

export default TemplateForm;